import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { artistListState, totalThumbnailUrlListState } from "../atom/atom";
import { addNewSong } from "../api/write.api";
import { Genre } from "../types/type";
import { fetchFilesFromStorage, generateSongId } from "../api/fetch.api";
import { useThumbnail } from "../hooks/useThumbnail";

interface INewSong {
  title: string;
  youtubeLink: string;
  youtubeId: string;
  thumbnailUrl: string;
  tagList: string[];
  isAI: boolean;
  makerUserId: string;
  tag: {
    genreList: string[];
    tagList: string[];
    [key: string]: any;
  };
}

function AddSongNoYoutubePage() {
  const [artistId, setArtistId] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [newSong, setNewSong] = useState<INewSong>({
    title: "",
    youtubeLink: "",
    youtubeId: "",
    thumbnailUrl: "",
    tagList: [],
    isAI: true,
    makerUserId: "Homebrew",
    tag: { genreList: [], tagList: [] },
  });
  const [tagList, setTagList] = useState<string[]>([]);
  const [genreList, setGenreList] = useState<string[]>([]);
  const artistList = useRecoilValue(artistListState);
  const [nextSongId, setNextSongId] = useState<string>("");

  const [totalArtistThumbnailList, setTotalArtistThumbnailList] =
    useRecoilState<{
      [key: string]: string[];
    }>(totalThumbnailUrlListState);

  const [selectedArtistThumbnailList, setSelectedArtistThumbnailList] =
    useState<string[]>([]);
  const [selectedThumbnailUrl, setSelectedThumbnailUrl] = useState<string>("");

  const { fetchAllArtistThumbnailList } = useThumbnail();

  const handleArtistSelect = (artistId: string, artistName: string) => {
    setArtistId(artistId);
    setArtistName(artistName);
    setNewSong((prevSong) => ({
      ...prevSong,
      tagList: [artistName],
    }));

    setSelectedArtistThumbnailList(totalArtistThumbnailList[artistName] || []);
  };

  const handleToggleGenre = (genre: string) => {
    setNewSong((prevSong) => {
      const genreIndex = prevSong.tag.genreList.indexOf(genre);
      if (genreIndex === -1) {
        return {
          ...prevSong,
          tag: {
            ...prevSong.tag,
            genreList: [...prevSong.tag.genreList, genre],
          },
        };
      } else {
        return {
          ...prevSong,
          tag: {
            ...prevSong.tag,
            genreList: prevSong.tag.genreList.filter((g) => g !== genre),
          },
        };
      }
    });
    setGenreList((prevList) =>
      prevList.includes(genre)
        ? prevList.filter((g) => g !== genre)
        : [...prevList, genre]
    );
  };

  const handleThumbnailSelect = (url: string) => {
    setNewSong((prevSong) => ({
      ...prevSong,
      thumbnailUrl: url,
    }));
    setSelectedThumbnailUrl(url); // 선택된 썸네일 URL 저장
  };

  const handleInputChange = (
    field: keyof INewSong,
    value: string | boolean
  ) => {
    setNewSong((prevSong) => ({
      ...prevSong,
      [field]: value,
    }));
  };

  const handleAddTag = (tag: string) => {
    setNewSong((prevSong) => ({
      ...prevSong,
      tag: { ...prevSong.tag, tagList: [...prevSong.tag.tagList, tag] },
    }));
    setTagList((prevList) =>
      prevList.includes(tag)
        ? prevList.filter((t) => t !== tag)
        : [...prevList, tag]
    );
  };

  const handleAddSong = async () => {
    try {
      await addNewSong({
        artistId,
        artistName,
        ...newSong,
      });
      // 폼 초기화
      setArtistId("");
      setArtistName("");
      setNewSong({
        title: "",
        youtubeLink: "",
        youtubeId: "",
        thumbnailUrl: "",
        tagList: [],
        isAI: true,
        makerUserId: "Homebrew",
        tag: { genreList: [], tagList: [] },
      });
    } catch (error) {
      console.error("Error adding songs: ", error);
      alert("Failed to add songs.");
    }
  };

  const clearArtistThumbnailCache = () => {
    localStorage.removeItem("totalArtistThumbnailList");
  };

  useEffect(() => {
    if (artistList.length) fetchAllArtistThumbnailList(); // 컴포넌트가 마운트될 때 모든 아티스트 썸네일을 가져옵니다.
  }, [artistList.length]);

  const calculateNextSongId = async () => {
    const newId = await generateSongId();
    setNextSongId(newId);
  };

  useEffect(() => {
    calculateNextSongId();
  }, [newSong]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h1>Add New Songs</h1>
        <div>
          <p>
            1. 썸네일 추가하고 싶으면, 스토리지 artistThumbnail/아티스트명 에
            파일 추가
          </p>
          <p>2. 초기화 버튼 눌러야 어드민에 뜸</p>
          <p>3. 처음에 로드해올때 시간 몇초 걸림.</p>
          <div className="sort-group">
            <button onClick={clearArtistThumbnailCache}>
              썸네일 캐시 초기화
            </button>
          </div>
        </div>
      </div>
      <div>다음 곡 예정 id : {nextSongId}</div>
      <div>
        <label>Select Artist:</label>
        {artistList.map((artist) => (
          <button
            key={artist.artistId}
            onClick={() =>
              handleArtistSelect(artist.artistId, artist.artistName)
            }
            style={{
              backgroundColor:
                artist.artistId === artistId ? "lightblue" : "white",
              borderColor: artist.artistId === artistId ? "blue" : "grey",
              borderWidth: 0.1,
              marginRight: 4,
            }}
          >
            {artist.artistName}
          </button>
        ))}
      </div>

      <div style={{ marginTop: "20px", borderBottom: "1px solid #ccc" }}>
        <div>
          <label>Title:</label>
          <input
            type="text"
            value={newSong.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
          />
        </div>
        <div>
          <label>Thumbnail URL:</label>
          <input type="text" value={newSong.thumbnailUrl} readOnly />
        </div>
        <div>
          <label>Tags (Press Enter to add):</label>
          <input
            type="text"
            defaultValue={"homebrewRecommend"}
            onKeyPress={(e) => {
              if (e.key === "Enter" && e.currentTarget.value) {
                handleAddTag(e.currentTarget.value);
                e.currentTarget.value = "";
              }
            }}
          />
          <div>
            {newSong.tag.tagList.map((tag, tagIndex) => (
              <span key={tagIndex} style={{ marginRight: "5px" }}>
                {tag}
              </span>
            ))}
          </div>
          <div>
            <label>Genre</label>
            {Object.values(Genre).map((genre) => (
              <button
                key={genre}
                onClick={() => handleToggleGenre(genre)}
                style={{
                  backgroundColor: newSong.tag.genreList.includes(genre)
                    ? "lightgreen"
                    : "white",
                  borderColor: newSong.tag.genreList.includes(genre)
                    ? "green"
                    : "grey",
                  borderWidth: 0.1,
                  marginRight: 4,
                }}
              >
                {genre}
              </button>
            ))}
            <div>
              {newSong.tag.genreList.map((genre, genreIndex) => (
                <span key={genreIndex} style={{ marginRight: "5px" }}>
                  {genre}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label>Is AI:</label>
          <input
            type="checkbox"
            checked={newSong.isAI}
            onChange={() => handleInputChange("isAI", !newSong.isAI)}
          />
        </div>
        <div>
          <label>Maker User ID:</label>
          <input
            type="text"
            value={newSong.makerUserId}
            onChange={(e) => handleInputChange("makerUserId", e.target.value)}
          />
        </div>
        <div
          className="thumbnail-container"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)", // 5개 열 설정
            gap: "10px", // 썸네일 간의 간격
          }}
        >
          {selectedArtistThumbnailList.length > 0 ? (
            selectedArtistThumbnailList.map((url, index) => (
              <div
                key={index}
                style={{
                  position: "relative",
                  margin: "1%",
                }}
              >
                <img
                  src={url}
                  alt={`Thumbnail ${index}`}
                  style={{
                    width: "100%", // 부모 div의 폭에 맞추기
                    cursor: "pointer",
                  }}
                  onClick={() => handleThumbnailSelect(url)} // Set thumbnail URL on click
                />
                {selectedThumbnailUrl === url && ( // Check if this thumbnail is selected
                  <span
                    style={{
                      position: "absolute",
                      top: 5,
                      left: 5,
                      color: "white",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      padding: "2px 5px",
                      borderRadius: "3px",
                    }}
                  >
                    ✔
                  </span>
                )}
              </div>
            ))
          ) : (
            <p>아티스트의 썸네일 X</p>
          )}
        </div>
      </div>

      <button type="button" onClick={handleAddSong}>
        데이터 전송
      </button>
    </div>
  );
}

export default AddSongNoYoutubePage;
